import { navigate } from '../../frame/route'

/**
 * Things the search field can *do*, as opposed to things it can find.
 *
 * The search is the one control that is over everything — ⌘K from anywhere, the magnifier in the bar —
 * so it is also the shortest way to the thing somebody wanted when they opened it. Typing `notes` and
 * pressing Enter writes a note; nothing was found, and that was never the point.
 *
 * They stand **above** the hits and never instead of them. A word that is both a place and a word in
 * somebody's documents has to answer as both, and deciding which of the two somebody meant is exactly
 * the kind of guess that makes a search feel like it is arguing.
 *
 * The list is deliberately tiny and deliberately here rather than in the service: an action is something
 * *this* application can do, and it has no business in a search language an MCP client inherits.
 */
export interface SearchAction {
  readonly id: string
  /** What the row says. A verb, because it is a thing that happens. */
  readonly label: string
  /** The quiet second line: what typing this was probably about. */
  readonly hint: string
  /** Whether this is what the line is asking for. */
  matches(line: string): boolean
  /** Does it, and goes wherever it leads. */
  run(): Promise<void>
}

/** Whether the line is one of these words, or the beginning of one of them. */
function beginsOneOf(line: string, words: readonly string[]): boolean {
  const asked = line.trim().toLowerCase()
  // Two characters, so that a search for `n` does not put an action above every hit in the space.
  return asked.length >= 2 && words.some((word) => word.startsWith(asked))
}

const WRITE_A_NOTE: SearchAction = {
  id: 'write-a-note',
  label: 'Write a note',
  hint: 'A new note on the pad — no title, no folder.',
  matches: (line) => beginsOneOf(line, ['notes', 'note', 'notiz', 'notizen']),
  /*
   * It opens the pad on a draft rather than making a note.
   *
   * Nothing exists until there is something to write down: a note that says nothing is not a note,
   * and this control has no text to give one. So it goes where the typing is, and the first keystroke
   * that lands is what makes the note.
   */
  run: () => {
    navigate('notes', ['new'])
    return Promise.resolve()
  },
}

/**
 * The same shape for an open point, and it is the reason this list is a list.
 *
 * The search is over everything, so it is also the shortest way to *write* something — and an open
 * point is the thing most likely to be typed in the two seconds somebody has. It leads to the field at
 * the top of the tasks with the cursor in it; nothing is created until there is a sentence.
 */
const ADD_A_TASK: SearchAction = {
  id: 'add-a-task',
  label: 'Add a task',
  hint: 'An open point — a sentence, and a date only if it has one.',
  matches: (line) => beginsOneOf(line, ['tasks', 'task', 'aufgabe', 'aufgaben', 'todo']),
  run: () => {
    navigate('tasks', ['new'])
    return Promise.resolve()
  },
}

/**
 * And the third: the surface, which is the one place in MyLib you go *to* rather than write into.
 *
 * It leads to the board somebody lands on rather than to the boards themselves, because "desk" is
 * what they typed and the desk is a board — the list of boards is a step further in and is reached
 * from the board that is open.
 */
const OPEN_THE_DESK: SearchAction = {
  id: 'open-the-desk',
  label: 'Open your desk',
  hint: 'The surface you arrange yourself — cards, sticky notes, frames.',
  matches: (line) => beginsOneOf(line, ['desk', 'board', 'boards', 'schreibtisch']),
  run: () => {
    navigate('desk', [])
    return Promise.resolve()
  },
}

export const ACTIONS: readonly SearchAction[] = [WRITE_A_NOTE, ADD_A_TASK, OPEN_THE_DESK]

export function actionsFor(line: string): SearchAction[] {
  return ACTIONS.filter((action) => action.matches(line))
}
